import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useCruisePurchaseDetails from 'hooks/Cruise/useCruisePurchaseDetails'
import TextButton from 'LuxKit/Button/TextButton'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  bookingId: string
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

const formatBold = (chunks: React.ReactNode) => <b>{chunks}</b>

function LesaCruisePurchaseSummary(props: Props) {
  const intl = useIntl()
  const { order, isPastOrder, bookingId, hideSelect, actions, payload, componentsMap, canEdit } = props

  const dispatch = useAppDispatch()

  const {
    name,
    bookingNumber,
    travelers,
    cabinInfo,
    cruiseItem,
    booking,
    offerSnapshot,
  } = useCruisePurchaseDetails(order, bookingId)

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id, canEditOrder: canEdit } }))
  }, [dispatch, order.id, canEdit])

  const portMessage = useMemo(() => {
    if (!offerSnapshot) return ''
    if (!offerSnapshot.isSamePort) {
      return intl.formatMessage(
        {
          id: 'QIgBlR-pxY6e3JYfXlCE',
          defaultMessage: 'Starts in <b>{departurePort}</b>, ends in <b>{returnPort}</b>',
          description: 'Cruise itinerary showing different departure and return ports',
        },
        {
          departurePort: offerSnapshot.departurePortName,
          returnPort: offerSnapshot.returnPortName,
          b: formatBold,
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'xBUmY4R89yijhqt19z79',
        defaultMessage: 'Starts and ends in <b>{port}</b>.',
        description: 'Cruise itinerary showing same departure and return port',
      },
      {
        port: offerSnapshot.departurePortName,
        b: formatBold,
      },
    )
  }, [intl, offerSnapshot])

  if (!booking || !offerSnapshot) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'resendConfirmationEmail']))

  const adultsLabel = travelers?.numberOfAdults ? intl.formatMessage({
    id: 'Vx2cRUftVl3vDZf1JGMB',
    defaultMessage: '{count, plural, one {# adult} other {# adults}}',
    description: 'Number of adults for cruise booking travellers in the cruises section',
  }, { count: travelers.numberOfAdults }) : undefined
  const childrenLabel = travelers?.numberOfChilds ? intl.formatMessage({
    id: 'B2uByvRYHyVP-iTSMpvH',
    defaultMessage: '{count, plural, one {# child} other {# children}}',
    description: 'Number of children for cruise booking travellers in the cruises section',
  }, { count: travelers.numberOfChilds }) : undefined
  const travellersLabel = adultsLabel && childrenLabel ? `${adultsLabel}, ${childrenLabel}` : adultsLabel

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineShipIcon size="XS" />}>{intl.formatMessage({
              id: 'u__hyjf6wB57H8GJVmt_',
              defaultMessage: 'Cruise',
              description: 'Category label for cruise purchase summary in the cruise section',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'FiKhWalE4NhPXShKy-Jr',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming cruise booking in the cruise section',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'QL5wtVk82LE3VuEJjJii',
              defaultMessage: 'Past',
              description: 'Status label for past cruise booking in the cruise offer or booking section',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >
                {portMessage}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {name}
              </LesaPurchaseSummaryTitle>
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="neutral-two">
                  {intl.formatMessage({
                    id: '8MmOvLP6RDiXb2Ersxy2',
                    defaultMessage: '{count, plural, one {# day} other {# days}}',
                    description: 'Number of days for cruise itinerary duration in the cruise section',
                  }, { count: offerSnapshot.duration })}
                </BodyText>
                <BodyText as="span" variant="small" colour="neutral-two">
                  {intl.formatMessage({
                    id: 'WYFrQqcggHZhNu8KVHnX',
                    defaultMessage: '{count, plural, one {# city} other {# cities}}',
                    description: 'Number of cities visited on cruise itinerary in the cruise section',
                  }, { count: offerSnapshot.numberOfCities })}
                </BodyText>
                {!!offerSnapshot.numberOfCountries &&
                  <BodyText as="span" variant="small" colour="neutral-two">
                    {intl.formatMessage({
                      id: 'LQprTJXP8GRDyA1AspwP',
                      defaultMessage: '{count, plural, one {# country} other {# countries}}',
                      description: 'Number of countries visited on cruise itinerary in the cruises section',
                    }, { count: offerSnapshot.numberOfCountries })}
                  </BodyText>}
              </LesaPurchaseSummarySeparator>
              <LesaPurchaseSummaryItineraryDates
                status={cruiseItem.status}
                showReservationDates={!!offerSnapshot.departureDate}
                startDate={offerSnapshot.departureDate}
                endDate={offerSnapshot.returnDate}
                bookingNumber={bookingNumber}
              />
              <LesaPurchaseSummarySeparator>
                {!!travelers?.numberOfAdults && (
                  <>
                    <BodyText as="span" variant="small" colour="neutral-three">
                      {travellersLabel}
                    </BodyText>
                    <BodyText as="span" variant="small" colour="neutral-three">{cabinInfo?.cabinType}</BodyText>
                  </>
                )}
              </LesaPurchaseSummarySeparator>
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'lrLx0XgdpeW2aPxqH7pa',
                  defaultMessage: 'Select',
                  description: 'Button text to select a cruise booking in the cruise offer or booking section',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaCruisePurchaseSummary
